import { Close, UploadFile } from '@mui/icons-material'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { LoadingButton } from '@mui/lab'
import { Box, Card, CardContent, CardHeader, CircularProgress, IconButton, LinearProgress, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../backend/ApiClient'
import API_URLS from '../backend/ApiUrls'
import LocalCache from '../backend/LocalCache'
import { IContractSchema } from '../definitions/ContractSchema'
import { ISignedURL, IUploadedResult } from '../definitions/Upload'
import Formatter from '../helpers/Formatter'
import Helper from '../helpers/Helper'
import UploadManager from '../managers/UploadManager'
import FileUpload from './controls/FileUpload'
import useInvalidate from './hooks/useInvalidate'
import useQuery, { buildQueryKey } from './hooks/useQuery'
import { snackbar } from './services/SnackbarService'
import useInvalidatePagination from './hooks/useInvalidatePagination'

interface P {
    contractId?: number
    marginTop?: number
    onFinish: (res: IUploadedResult) => void
    onClose?: () => void
}

export default function NewContractModal({ contractId, marginTop, onFinish, onClose }: P) {
    const [selectedFiles, setSelectedFiles] = useState<File[]>()
    const [modalStatus, setModalStatus] = useState<'insert' | 'loading' | 'success' | 'failed'>('insert')
    const [selectedSchemaKey, setSelectedSchemaKey] = useState<string>()
    const [contractName, setContractName] = useState<string>()
    const [businessKey, setBusinessKey] = useState<string>()

    const invalidatePendingsContractListSmartTable = useInvalidatePagination({ queryKey: buildQueryKey(API_URLS.os.getPendingContracts + '/*') })
    const invalidateApprovals = useInvalidate({ queryKey: buildQueryKey('get-approvals') })

    const { status: schemaStatus, response: allSchemas } = useQuery({
        queryKey: buildQueryKey(API_URLS.os.getAllContractSchemas + '/*'),
        queryFn: () => ApiClient.getAllContractSchemas(),
        cache: true
    })

    const upload = async (signs: ISignedURL[], files: File[], actualContractId: number, contractUUID: string) => {
        const res = await UploadManager.uploadContractFiles(signs, files, actualContractId, contractUUID)
        if (Helper.hasVal(res)) {
            handleSuccess(res)
        } else {
            setModalStatus('failed')
        }
    }

    const handleSuccess = async (result: IUploadedResult) => {
        setModalStatus('success')
        LocalCache.setStale('contracts')
        LocalCache.setStale('files')
        snackbar.show({ severity: 'success', text: 'Operation completed successfully' })
        onFinish(result)
        invalidatePendingsContractListSmartTable()
        invalidateApprovals()
        try {
            await ApiClient.refreshAllMaterializedViews()
        } catch (error) {
            console.error('Error refreshing materialized views:', error)
        }
    }

    const onCreateContract = async () => {
        setModalStatus('loading')
        try {
            let resultContractId = contractId
            let resultContractUUID = ''

            if (!resultContractId) {
                const newContract = await ApiClient.createNewContract(
                    selectedSchemaKey,
                    businessKey,
                    contractName
                )
                resultContractId = newContract.id
                resultContractUUID = newContract.uuid
            }

            if (selectedFiles?.length) {
                const signedURLsResult = await ApiClient.getSignedURLs(selectedFiles.length)

                await upload(
                    signedURLsResult,
                    selectedFiles,
                    resultContractId,
                    resultContractUUID
                )
            } else {
                handleSuccess(
                    { contractId: resultContractId, uploadedfiles: [], contractUUID: resultContractUUID }
                )
            }
        } catch (error) {
            console.error('Error creating contract:', error)
            setModalStatus('failed')
            snackbar.show({ severity: 'error', text: 'Failed to create contract' })
        }
    }

    if (schemaStatus === 'loading') {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' mt={4}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box margin={(marginTop ?? 80) + 'px auto auto'} maxWidth={640}>
            <Card>
                <CardHeader
                    title='Create New Contract'
                    action={(
                        <IconButton onClick={onClose} size='small'>
                            <Close />
                        </IconButton>
                    )}
                />
                <CardContent>
                    <Box display='flex' alignItems='flex-start' gap={2} mb={2}>
                        <Box flex={1} display='flex' flexDirection='column' gap={0.5}>
                            <Typography variant='body2' fontWeight={500}>
                                Contract name
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                value={contractName}
                                onChange={e => setContractName(e.target.value)}
                                size='small'
                            />
                        </Box>
                        <Box flex={1} display='flex' flexDirection='column' gap={0.5}>
                            <Typography variant='body2' fontWeight={500}>
                                Select contract schema
                            </Typography>
                            <Select
                                fullWidth
                                required
                                size='small'
                                value={selectedSchemaKey ?? ''}
                                onChange={e => setSelectedSchemaKey(e.target.value)}
                                disabled={modalStatus !== 'insert'}>
                                {allSchemas?.map((schema: IContractSchema) => (
                                    <MenuItem key={schema?.foreignKey} value={schema?.foreignKey}>
                                        {schema?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Box>
                    <Box display='flex' alignItems='flex-start' gap={2} mb={2}>
                        <Box flex={1} display='flex' flexDirection='column' gap={0.5}>
                            <Typography variant='body2' fontWeight={500}>
                                Business key
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                value={businessKey}
                                onChange={e => setBusinessKey(e.target.value)}
                                size='small'
                            />
                        </Box>
                    </Box>
                    <Box display='flex' alignItems='center' gap={1} mb={2}>
                        <Typography fontWeight={500}>Upload files</Typography>
                        <Typography fontWeight={400}>(optional)</Typography>
                    </Box>
                    <FileUpload disable={modalStatus !== 'insert'} setSelectedFiles={setSelectedFiles} />

                    {selectedFiles?.map((file, i) => (
                        <Box key={i} display='flex' flexDirection='row' alignItems='center' gap={2} mb={1} mt={2}>
                            <UploadFile color='primary' />
                            <Box flex={1}>
                                <Typography fontWeight={500}>{file.name}</Typography>
                                <Typography variant='caption'>
                                    {Formatter.bytes(file.size, 1)}
                                    {' • '}
                                    {modalStatus === 'insert'
                                        ? 'Ready to Upload'
                                        : modalStatus === 'loading'
                                            ? 'Loading'
                                            : modalStatus === 'success' ? 'Success' : 'Failed'}
                                </Typography>
                                {modalStatus === 'loading' && <LinearProgress sx={{ mt: 1 }} />}
                            </Box>
                        </Box>
                    ))}
                    <Box display='flex' justifyContent='flex-end' mt={2}>
                        <LoadingButton
                            loading={modalStatus === 'loading'}
                            loadingPosition='start'
                            variant='contained'
                            startIcon={<UploadFileRoundedIcon />}
                            onClick={onCreateContract}
                            disabled={!contractName || !selectedSchemaKey || !businessKey}>
                            Create Contract
                        </LoadingButton>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}
